<template>
  <div
    class="flex fixed top-0 lg:top-12 z-40 transform"
    :class="subSideMenuClasses"
    ref="subsidemenu"
    id="subsidemenu"
  >
    <button
      v-show="mobileSubMenuOpen"
      class="flex items-center sticky top-0 bg-secondary dark:bg-navy-800 backdrop-blur backdrop-filter bg-opacity-90 focus:bg-secondary-600 dark:focus:bg-white/[.06] focus:outline-none lg:focus:bg-transparent lg:hidden justify-left w-full py-4 border-b-2 border-primary-50 border-opacity-5 z-10"
      @click="toggleMobileSubMenu(false)"
    >
      <font-awesome-icon :icon="['fas', 'arrow-left']" class="ml-4 text-white" />
      <p class="ml-4 font-medium text-lg text-white uppercase">
        Main Menu
      </p>
    </button>
    <ul
      v-if="showSectionItems"
      class="subnavs-height"
    >
      <li v-for="option in options" :key="option.label" tabindex="-1">
        <div v-if="option.module && checkSidemenuPermissions(option)">
          <ModuleSubnav :module="option" :subsection="subsection" />
        </div>
        <div v-if="option.subnav && !option.module && checkSidemenuPermissions(option)">
          <OptionWithSubnav :option="option" :subsection="subsection" />
        </div>
        <div v-if="!option.subnav && checkSidemenuPermissions(option)" tabindex="-1">
          <router-link :to="urlLink(option.route)" tabindex="-1">
            <button
              @click="select()"
              class="subSideMenuButton px-8 py-6 w-full text-left text-md uppercase text-sm md:text-xs font-medium text-white outline-none focus:bg-secondary-600 dark:focus:bg-white/[.06] focus:outline-none active:outline-none transition-all select-none"
              :class="subnavClass(option.route)"
            >
              {{ option.label }}
            </button>
          </router-link>
        </div>
      </li>
    </ul>
    <!-- SIDEWAYS INDICATOR -->
    <section v-else class="h-max flex justify-center items-start">
      <button
        class="uppercase text-secondary dark:text-gray-400/80 font-extrabold tracking-wider whitespace-nowrap translate-sidebtn"
        @click="collapse"
      >
        {{ sidewaysIndicator }}
      </button>
    </section>
    <!-- COLLAPSE BUTTON -->
    <div class="sidebar-footer bottom-0 right-0">
      <button
        v-if="screen.lg"
        @click="collapse"
        class="sidebar-footer__collapse"
      >
        <font-awesome-icon :icon="['fas', sectionMenuCollapsed ? 'chevron-right' : 'chevron-left']" class="sidebar-footer__collapse-icon" />
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from "vuex"
import OptionWithSubnav from "./OptionWithSubnav.vue"
import ModuleSubnav from "./ModuleSubnav.vue";

export default {
  name: "SubSideMenu",
  components: {
    OptionWithSubnav,
    ModuleSubnav
  },
  props: {
    subsection: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isMounted: false,
      tabBack: false
    }
  },
  computed: {
    ...mapState([
      "screen",
      "orientation",
      "availableModules"
    ]),
    ...mapGetters([
      "mobileSubMenuOpen",
      "mobileSideMenuOpen",
      "mainMenuCollapsed",
      "sectionMenuCollapsed",
      "modalOpen",
      "getIsTenantSuperuser",
      "getUserManagementAccess",
      "getPermissionSet",
      "getUserPermissions",
      "getAvailableFeatures",
      "getAvailableViews",
      "getPOSSystem",
      "getSiteCommandControllerFeatures"
    ]),
    route() {
      return this.$route
    },
    modulePermissions() {
      return this.getUserPermissions[this.route.meta.module]
    },
    availableFeatures() {
      return this.getAvailableFeatures?.[this.route.meta.module]
    },
    sidewaysIndicator() {
      const pathName = this.$route.name
      const subPage = this.$route.params.subPage ?? this.$route.meta.subPage

      // display `sidewayDisplay` option for subnav option if it exists
      if (subPage) {
        for (let option of this.options) {
          if (option.subnav) {
            for (let subOption of option.subnav) {
              if (this.$route.path.includes(subOption.route)) {
                if (subOption.sidewayDisplay) return subOption.sidewayDisplay
                break
              }
            }
          }
        }
      }

      // display `sidewayDisplay` option for non-subnav option if it exists
      else {
        for (let option of this.options) {
          if (this.$route.path.includes(option.route)) {
            if (option.sidewayDisplay) return option.sidewayDisplay
            break
          }
        }
      }

      // otherwise generate sideway display based on path name
      if (!subPage) return pathName.replace(/-/g, " ").replace(/_/g, " ")
      else return subPage.replace(/-/g, " ").replace(/_/g, " ")
    },
    subSideMenuClasses() {
      let classes = "bg-secondary dark:bg-navy-900 backdrop-filter backdrop-blur bg-opacity-90 shadow-xl p-0 z-50 transform w-4/5 -mt-1 top-20 lg:justify-between "
      if (this.isMounted) classes += "transition-all "
      classes += (this.screen.lg) ? "h-sidemenu " : "h-full "
      if (!this.mobileSubMenuOpen || !this.mobileSideMenuOpen) {
        classes += "-translate-x-full overflow-auto lg:transform-none "
      }
      classes += `flex flex-col sm:w-3/5 lg:bg-white dark:lg:bg-navy-900 lg:top-12 lg:z-20 lg:border-r-4 lg:border-gray-light dark:lg:border-gray-800 lg:border-opacity-80 lg:rounded-none lg:mt-0 `
      classes += this.mainMenuCollapsed ? "lg:left-16 " : "lg:left-52 "
      if (this.sectionMenuCollapsed) classes += "lg:w-16 "
      else classes += "lg:w-64 overflow-auto  "
      return classes
    },
    showSectionItems() {
      if (!this.screen.lg) return true
      else if (!this.sectionMenuCollapsed) return true
      return false
    },
    path() {
      return this.$route.fullPath
    }
  },
  watch: {
    modalOpen(val) {
      if (val && this.mobileSubMenuOpen) {
        this.toggleMobileSubMenu(true)
      }
    },
  },
  mounted() {
    this.$router.isReady()
    .then(() => {
      if (this.$route.path.substring(1).includes("/") && !this.sectionMenuCollapsed) this.collapse()
      setTimeout(() => this.isMounted = true, 1000)
    })
    if (!this.screen.lg) {
      setTimeout(this.toggleMobileSubMenu(true), 500)
    }
    document.addEventListener("focusin", this.handleTab, true)
  },
  beforeUnmount() {
    document.removeEventListener("focusin", this.handleTab, true)
  },
  methods: {
    ...mapMutations([
      "CLOSE_MOBILEMENU",
      "toggleMobileSubMenu",
      "collapseSectionMenu",
    ]),
    select() {
      this.CLOSE_MOBILEMENU()
    },
    collapse() {
      this.collapseSectionMenu(!this.sectionMenuCollapsed)
    },
    urlLink(path) {
      return this.$route.path.includes(this.subsection)
        ? `/${this.subsection}/${path}`
        : path;
    },
    subnavClass(route) {
      let classes = "lg:pl-3 lg:pr-2 lg:py-2 lg:text-gray-600 dark:lg:text-white/60 lg:font-medium lg:border-l-4 "
      if (this.path.includes(route)) {
        classes += "bg-secondary-600 dark:bg-white/[.06] text-opacity-100 dark:lg:text-white/90 lg:bg-gray-light/80 dark:lg:bg-white/[.04] lg:focus:bg-gray-light dark:lg:focus:bg-white/[.06] lg:border-primary dark:lg:border-primary-300/70 "
      }
      else {
        classes += "bg-transparent lg:bg-opacity-100 lg:bg-white dark:bg-navy-900 lg:border-white dark:lg:border-navy-900 lg:hover:bg-gray-lightest/80 dark:lg:hover:bg-white/[.04] lg:hover:border-primary dark:lg:hover:border-primary-300/70 lg:focus:bg-gray-lightest lg:focus:bg-opacity-80 lg:focus:border-primary lg:focus:bg-gray-lightest/80 dark:lg:focus:bg-white/[.04] dark:lg:focus:border-primary-300/70 lg:active:outline-none "
      }
      return classes
    },
    checkSidemenuPermissions(option) {
      // check if option is a module grouping
      if (option.module) {
        // skip if no access to module
        if (!this.availableModules.includes(option.module)) return false
        // check that at least one subnav option is available for module
        let hasAccess = false
        for (let subnav of option.subnav) {
          if (this.checkSidemenuPermissions(subnav)) hasAccess = true
        }
        return hasAccess
      }

      // need to check if feature is available at site for Reports and Configuration
      let moduleFeatures = {
        // if option name is relevant to either module, only show if site has access to module
        site_track: ["asset", "assets", "inventory", "purchase_orders", "locations", "manufacturers", "vendors"],
        site_scan: ["chemical", "thresholds"]
      }
      if (this.subsection === "Configuration" || this.subsection === "Reports") {
        for (let module of Object.keys(moduleFeatures)) {
          if (moduleFeatures[module].includes(option.feature) && !this.availableModules.includes(module)) return false
        }
      }
      // check Site Command version features
      if (this.subsection === "SiteCommand" ) {
        if (option.feature === "edl_cil") {
          if (!this.getSiteCommandControllerFeatures?.includes("EDL_CIL")) return false
        }
        if (option.feature === "assistance_tool") {
          if (!this.getSiteCommandControllerFeatures?.includes("Bumps_Mirror")
            && !this.getSiteCommandControllerFeatures?.includes("Bumps_Wrap_HB")
            && !this.getSiteCommandControllerFeatures?.includes("Control_Word")
            && !this.getSiteCommandControllerFeatures?.includes("Omni_Control")
            && !this.getSiteCommandControllerFeatures?.includes("Requeue_Control_Word")
            && !this.getSiteCommandControllerFeatures?.includes("Omni_Vac")
            && !this.getSiteCommandControllerFeatures?.includes("Airgate_Table")
            && !this.getSiteCommandControllerFeatures?.includes("Dry_Run")
            && !this.getSiteCommandControllerFeatures?.includes("Conveyor_Speed_Config")
            && !this.getSiteCommandControllerFeatures?.includes("PID_Attack_Module")
            && !this.getSiteCommandControllerFeatures?.includes("Blower_Flips")
          ) return false
        }
      }
      // show Washlink only is site has access to washlink
      if (option.feature === "washlink" && this.getPOSSystem !== "washlink") return false
      // always show if site includes relevant module AND user is superuser
      if (this.getIsTenantSuperuser) return true
      // hide internal options from non-superusers
      if (option.internal) return false
      // show Users & Permissions + chem thresholds if user has user_management privileges
      if (this.getUserManagementAccess && (
        option.feature === "user_management" || option.feature === "thresholds"
      )) return true
      if (!option.feature && option.subnav) {
        // if option has subnav, check if any subnav options are available
        for (let subOption of option.subnav) {
          if (this.checkSidemenuPermissions(subOption)) return true
        }
        return false
      }
      // otherwise base on user permissions
      return this.availableFeatures[option?.feature]
    },
    handleTab(e) {
      if (this.screen.lg || !this.$refs.subsidemenu || !this.mobileSubMenuOpen || !this.mobileSideMenuOpen) return
      const tabbedInsideSidemenu = e.target === this.$refs.subsidemenu
        || this.$refs.subsidemenu.contains(e.target)
      if (!tabbedInsideSidemenu) {
        const closeMenuBtn = document.querySelector("#closeSidemenuBtn")
        closeMenuBtn.focus() 
      }
    }
  },
}
</script>

<style scoped>
.subSideMenuButton {
  min-height: 64px !important;
  line-height: 1.4;
}
.translate-sidebtn {
  transform-origin: left;
  transform: translate(50%, 35%) rotate(90deg) ;
}
@media (max-width: 1024px) {
  .subnavs-height {
    height: calc(100% - 8.5rem);
    overflow-y: scroll;
  }
}
/* Footer (version + collapse) */
.sidebar-footer {
  display: flex;
  align-items: center;
  justify-content: end;
  padding: 0.5rem 0.625rem;
  width: 100%;
}
.sidebar-footer__collapse {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: rgba(223, 223, 223, 0.5);
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #000000;
}
.sidebar-footer__collapse:hover {
  background: rgba(223, 223, 223, 0.8);
  color: #000000;
}

.sidebar-footer__collapse-icon {
  font-size: 0.625rem;
}

[data-theme="dark"] .sidebar-footer__collapse {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.35);
}

[data-theme="dark"] .sidebar-footer__collapse:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
}


/* ── Sub-side-menu (section navigation panel) ───── */
/* [data-theme="dark"] #subsidemenu { */
  /* background: rgba(8, 14, 24, 0.97); */
  /* border-color: rgba(255, 255, 255, 0.06); */
/* } */

/* Menu item buttons on desktop (lg:bg-white → dark) */
/* [data-theme="dark"] .subSideMenuButton {
  color: rgba(255, 255, 255, 0.65) !important;
  border-left-color: transparent !important;
}

[data-theme="dark"] .subSideMenuButton:hover {
  background: rgba(255, 255, 255, 0.06) !important;
  border-left-color: rgba(0, 146, 191, 0.50) !important;
} */

/* Active state for sub-menu items */
/* [data-theme="dark"] .subSideMenuButton.lg\:bg-gray-light {
  background: rgba(255, 255, 255, 0.08) !important;
  border-left-color: #0092bf !important;
} */

/* Sideways collapsed label */
/* [data-theme="dark"] .translate-sidebtn {
  color: rgba(255, 255, 255, 0.35) !important;
} */

/* Collapse chevron button */
/* [data-theme="dark"] #subsidemenu .text-gray-600 {
  color: rgba(255, 255, 255, 0.30) !important;
}

[data-theme="dark"] #subsidemenu .text-gray-600:hover {
  background: rgba(255, 255, 255, 0.08) !important;
  color: rgba(255, 255, 255, 0.75) !important;
} */

/* ── Section sidebar (SubSideMenu) ──────────────── */
@media (min-width: 1024px) {

  /* Sidebar container: lg:bg-white → dark navy */
  /* [data-theme="dark"] #subsidemenu {
    background-color: rgba(10, 14, 22, 0.98) !important;
    border-color: rgba(255, 255, 255, 0.04) !important;
  } */

  /* Sidebar item text: lg:text-gray-600 → light */
  /* [data-theme="dark"] .subSideMenuButton {
    color: rgba(255, 255, 255, 0.55) !important;
  } */

  /* Selected sidebar item */
  /* [data-theme="dark"] .subSideMenuButton.lg\:bg-gray-light {
    background-color: rgba(0, 146, 191, 0.10) !important;
    color: rgba(255, 255, 255, 0.90) !important;
    border-left-color: var(--primary) !important;
  } */

  /* Unselected sidebar item: remove white border & bg */
  /* [data-theme="dark"] .subSideMenuButton.lg\:bg-white {
    background-color: transparent !important;
    border-left-color: transparent !important;
  } */

  /* Hover state */
  /* [data-theme="dark"] .subSideMenuButton:hover {
    background-color: rgba(255, 255, 255, 0.04) !important;
    color: rgba(255, 255, 255, 0.85) !important;
  } */

  /* Override Tailwind responsive text color */
  /* [data-theme="dark"] .lg\:text-gray-600 {
    color: rgba(255, 255, 255, 0.55) !important;
  } */
}

/* Sidebar divider */
/* [data-theme="dark"] .border-gray-light {
  border-color: rgba(255, 255, 255, 0.04) !important;
}

[data-theme="dark"] .bg-gray-light {
  background-color: rgba(255, 255, 255, 0.06) !important;
}

[data-theme="dark"] .bg-gray-lightest {
  background-color: rgba(255, 255, 255, 0.03) !important;
} */
</style>